import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
} from '@mui/material';

import { useAuth } from './AuthProvider';
import { toApiError } from '@/api/errors';
import { brand } from '@/theme/theme';

const schema = z.object({
  email: z.email('Enter a valid email address.'),
  password: z.string().min(1, 'Enter your password.'),
});

type FormValues = z.infer<typeof schema>;

/** Sign-in screen. */
export function LoginPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = async (values: FormValues) => {
    setSubmitError(null);

    try {
      const user = await signIn(values);

      // Back to wherever they were headed before being bounced here, so a
      // bookmarked deep link survives the sign-in.
      const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname;

      navigate(user.mustChangePassword ? '/change-password' : (from ?? '/dashboard'), {
        replace: true,
      });
    } catch (error) {
      // The API deliberately returns one message for both a wrong password and
      // an unknown email — distinguishing them would let anyone enumerate which
      // addresses have accounts. Shown verbatim.
      setSubmitError(toApiError(error).message);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'grid',
        placeItems: 'center',
        px: 2,
        bgcolor: 'background.default',
      }}
    >
      <Stack spacing={3} sx={{ width: '100%', maxWidth: 400 }}>
        <Stack spacing={1.5} sx={{ alignItems: 'center' }}>
          <Box
            component="img"
            src="/asterlogic-logo.jpeg"
            alt="AsterLogic CRM"
            sx={{ width: '100%', maxWidth: 220, height: 'auto' }}
          />
        </Stack>

        <Card>
          <CardContent sx={{ p: 3 }}>
            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <Stack spacing={2.5}>
                {submitError && <Alert severity="error">{submitError}</Alert>}

                <TextField
                  {...register('email')}
                  label="Email"
                  type="email"
                  autoComplete="username"
                  autoFocus
                  error={!!errors.email}
                  helperText={errors.email?.message}
                />

                <TextField
                  {...register('password')}
                  label="Password"
                  type="password"
                  autoComplete="current-password"
                  error={!!errors.password}
                  helperText={errors.password?.message}
                />

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  loading={isSubmitting}
                  sx={{ background: brand.gradient, py: 1.2 }}
                >
                  Sign in
                </Button>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}
